import React, { createContext, useContext, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { DashboardQuery } from '~/QueryHooks/DashboardQuery'
import { FarmerMilkMoney, Month, MilkMoneyDataType } from '~/api/services/dashboard'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

export const formatMonthLabel = (key: Date | string): string =>
  dayjs(key).format('MMM YYYY')

interface MilkMoneyContextType {
  milkMoneyData: MilkMoneyDataType | undefined
  milkMoneyOptions: { farmers: FarmerMilkMoney[]; months: Month[] } | undefined
  selectedFarmer: string
  selectedMonth: string
  setSelectedFarmer: (id: string) => void
  setSelectedMonth: (month: string) => void
  isLoading: boolean
}

const MilkMoneyContext = createContext<MilkMoneyContextType | null>(null)

export const useMilkMoney = (): MilkMoneyContextType => {
  const ctx = useContext(MilkMoneyContext)
  if (!ctx) throw new Error('useMilkMoney must be used within MilkMoneyProvider')
  return ctx
}

export const MilkMoneyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    data: milkMoneyOptions,
    isLoading: isLoadingOptions,
    refetch: fetchOptions,
    isFetching: isFetchingOptions
  } = DashboardQuery.getDashboardMilkMoneyOptions()

  const [selectedFarmer, setSelectedFarmer] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')

  const optionsReady = !isLoadingOptions && !isFetchingOptions

  const effectiveFarmer = optionsReady ? (selectedFarmer || milkMoneyOptions?.farmers?.[0]?.farmerId || '') : ''
  const effectiveMonth = optionsReady ? (selectedMonth || (milkMoneyOptions?.months?.[0]?.key
    ? String(milkMoneyOptions.months[0].key)
    : '')) : ''

  const {
    data: milkMoneyData,
    isLoading: isLoadingMoneyData,
    isFetching: isFetchingMoneyData,
    refetch: fetchData
  } = DashboardQuery.getDashboardMilkMoneyData(
    isLoadingOptions,
    {
      farmerId: effectiveFarmer,
      month: effectiveMonth
    })

  useEffect(() => {
    fetchData()
    fetchOptions()
    return () => {
      queryClient.removeQueries('dashboard-milk-money-data')
      queryClient.removeQueries('dashboard-milk-money-options-data')
    }
  }, [t])

  return (
    <MilkMoneyContext.Provider value={{
      milkMoneyData,
      milkMoneyOptions,
      selectedFarmer: effectiveFarmer,
      selectedMonth: effectiveMonth,
      setSelectedFarmer,
      setSelectedMonth,
      isLoading: isLoadingMoneyData || isFetchingMoneyData || isLoadingOptions || isFetchingOptions
    }}>
      {children}
    </MilkMoneyContext.Provider>
  )
}
